import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import CountryCodeSelect, { countryCodes } from './CountryCodeSelect';
import { useGeo } from '../context/GeoContext';
import { submitLead } from '../services/api';

const ContactModal = ({ isOpen, onClose, title = "Get Started Today", packageTitle = '', packageAmount = 0 }) => {
  const geoData = useGeo();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    countryCode: '+1',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  useEffect(() => {
    if (geoData?.data?.countryCode) {
      const match = countryCodes.find(c => c.label === geoData.data.countryCode);
      if (match) {
        setFormData(prev => ({ ...prev, countryCode: match.code }));
      }
    }
  }, [geoData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/[^0-9]/g, '');
      if (digitsOnly.length <= 15) {
        setFormData(prev => ({ ...prev, phone: digitsOnly }));
      }
      if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'email' && value.trim()) {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value)) setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    }
    if (name === 'phone' && value.trim()) {
      const len = value.replace(/[^0-9]/g, '').length;
      if (len < 7 || len > 15) setErrors(prev => ({ ...prev, phone: 'Please enter a valid phone number (7-15 digits)' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email)) newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const len = formData.phone.replace(/[^0-9]/g, '').length;
      if (len < 7 || len > 15) newErrors.phone = 'Please enter a valid phone number (7-15 digits)';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }

    setIsSubmitting(true);
    setApiError('');

    try {
      const selected = countryCodes.find(c => c.code === formData.countryCode) || countryCodes[0];
      const result = await submitLead({
        name: formData.fullName,
        email: formData.email,
        countryDialCode: formData.countryCode.replace('+', ''),
        country: selected.label,
        phoneNumber: formData.phone,
        description: formData.description,
        packageTitle,
        packageAmount,
        geoData,
      });

      if (result.success) {
        onClose();
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        setApiError(result.error);
      }
    } catch (err) {
      setApiError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[92vh] sm:max-h-[80vh]">
        <button onClick={onClose} className="absolute top-2 right-2 z-10 p-1 sm:p-1.5 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200" data-testid="modal-close-btn">
          <X className="h-4 w-4 text-gray-600" />
        </button>

        <div className="hidden md:block md:w-1/3 relative">
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&w=800&h=1000&fit=crop" alt="Corporate trademark consultation" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2d2d51] via-[#2d2d51]/60 to-[#2d2d51]/30" />
          <div className="relative flex flex-col justify-end p-6 h-full text-white">
            <h3 className="font-heading text-lg font-extrabold mb-2 tracking-tight">Protect Your Brand</h3>
            <p className="text-gray-300 text-xs leading-relaxed">Secure your trademark, logo, and slogan with federal protection across all 50 states.</p>
          </div>
        </div>

        <div className="md:w-2/3 p-3 sm:p-5 overflow-y-auto">
          <h2 className="font-heading text-base sm:text-xl font-bold text-[#2d2d51] mb-0.5">{title}</h2>
          <p className="text-gray-500 text-[11px] sm:text-xs mb-2 sm:mb-3">Fill out the form and our team will contact you shortly.</p>

          {apiError && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-md" data-testid="modal-api-error">
              <p className="text-xs text-red-700">{apiError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-2">
            <div>
              <label htmlFor="fullName" className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-0.5">Full Name *</label>
              <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange}
                className={`w-full px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-lime focus:border-transparent transition-all text-black bg-white ${errors.fullName ? 'border-red-500' : 'border-slate-300'}`}
                placeholder="John Doe" data-testid="modal-input-fullname" />
              {errors.fullName && <p className="mt-0.5 text-[10px] text-red-600">{errors.fullName}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-0.5">Email Address *</label>
              <input type="text" id="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur}
                className={`w-full px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-lime focus:border-transparent transition-all text-black bg-white ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
                placeholder="john@example.com" data-testid="modal-input-email" />
              {errors.email && <p className="mt-0.5 text-[10px] text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-0.5">Phone Number *</label>
              <div className="flex gap-1.5">
                <CountryCodeSelect value={formData.countryCode} onChange={(code) => handleChange({ target: { name: 'countryCode', value: code } })} className="w-24 sm:w-28" />
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} onBlur={handleBlur} maxLength={15}
                  className={`flex-1 px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-lime focus:border-transparent transition-all text-black bg-white ${errors.phone ? 'border-red-500' : 'border-slate-300'}`}
                  placeholder="1234567890" data-testid="modal-input-phone" />
              </div>
              {errors.phone && <p className="mt-0.5 text-[10px] text-red-600">{errors.phone}</p>}
            </div>

            <div>
              <label htmlFor="description" className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-0.5">Description</label>
              <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows="2"
                className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lime focus:border-transparent transition-all text-black bg-white resize-none"
                placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" data-testid="modal-input-description" />
            </div>

            <button type="submit" disabled={isSubmitting} data-testid="modal-submit-btn"
              className="w-full bg-lime hover:bg-lime-light text-white font-bold py-2 sm:py-2.5 px-6 rounded-xl transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm">
              {isSubmitting ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Submitting...</span> : 'Register Your Trademark'}
            </button>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContactModal;
